import React, { useState } from "react";
import style from "../CommonStyles/commonformstyles.module.scss";
import { FaCloudArrowUp } from "react-icons/fa6";
import { Select, MenuItem } from "@mui/material";
import useMultipleFileUploadAws from "../../../../hooks/AWSFileUpload/useMultipleFileUploadAws";
import uploadAnimation from "../../../../assets/gif/uploadAnimation.gif";
import { useSelector } from "react-redux";
import { useSubmitKyc } from "../../../../hooks/partnerHook/usePartnerKyc";
import { useNavigate } from "react-router-dom";
import { BsPersonCircle } from "react-icons/bs";
import { HiClipboardDocumentList } from "react-icons/hi2";
import { TbCameraSelfie } from "react-icons/tb";
import { ImEye } from "react-icons/im";
import { IoIosArrowBack } from "react-icons/io";

function KycDocumentsForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    documentType: "",
    documentCountry: "",
    documentNumber: "",
    documentFile: null,
    documentUrl: "",
    selfieImage: null,
    selfieUrl: "",
  });

  const { mutate: submitKyc } = useSubmitKyc(navigate);
  const userData = useSelector((state) => state.partner.user);
  let partnerId = userData?.partner?.id || null;

  const { uploadFileToS3, mediaUrls } = useMultipleFileUploadAws();
  const [uploadPreview, setUploadPreview] = useState(uploadAnimation);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const [photoCaptured, setPhotoCaptured] = useState(false);
  const [selfieUploadSuccess, setSelfieUploadSuccess] = useState(false);

  const handleInputChange = (event) => {
    const { name, value, files } = event.target;

    if (name === "documentFile" || name === "selfieImage") {
      const file = files[0];
      if (file) {
        setFormData((prevData) => ({
          ...prevData,
          [name]: file,
        }));

        const reader = new FileReader();
        reader.onload = () => {
          if (name === "documentFile") {
            setUploadPreview(reader.result);
          } else if (name === "selfieImage") {
            setSelfieUploadSuccess(false);
          }
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleBrowseClick = () => {
    document.getElementById("kyc-file-input").click();
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    const validFormats = ["image/png", "image/jpeg"];
    if (!validFormats.includes(file.type)) {
      setErrorMessage("Invalid file format. Only PNG & JPG are allowed.");
      return;
    }

    setErrorMessage("");
    setFormData((prevData) => ({
      ...prevData,
      documentFile: file,
    }));

    const reader = new FileReader();
    reader.onload = () => {
      setUploadPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCancel = () => {
    setFormData((prevData) => ({
      ...prevData,
      documentFile: null,
      documentUrl: "",
    }));
    setUploadPreview(uploadAnimation);
    setUploadSuccess(false);
  };

  const handleUpload = async () => {
    if (!formData.documentFile) return;
    setUploading(true);

    try {
      await uploadFileToS3(formData.documentFile);

      const checkUrlInterval = setInterval(() => {
        if (mediaUrls[formData.documentFile.name]) {
          clearInterval(checkUrlInterval);

          const uploadedUrl = mediaUrls[formData.documentFile.name];

          setFormData((prevData) => ({
            ...prevData,
            documentUrl: uploadedUrl,
          }));

          setUploadSuccess(true);
          setUploading(false);
        }
      }, 500);
    } catch (error) {
      console.error(" Upload Error:", error);
      setUploading(false);
      setErrorMessage("Failed to upload the file. Please try again.");
    }
  };

  const handleTakeSelfie = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setVideoStream(stream);
      setIsCameraOpen(true);
      setPhotoCaptured(false);

      setTimeout(() => {
        const video = document.getElementById("selfie-video");
        if (video) {
          video.srcObject = stream;
        }
      }, 500);
    } catch (error) {
      setErrorMessage("Failed to access camera. Please allow camera access.");
    }
  };

  const handleCapturePhoto = () => {
    if (!videoStream) return;

    const video = document.getElementById("selfie-video");
    if (!video) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageDataUrl = canvas.toDataURL("image/png");
    setFormData((prevData) => ({
      ...prevData,
      selfieImage: imageDataUrl,
    }));

    setPhotoCaptured(true);

    videoStream.getTracks().forEach((track) => track.stop());
    setVideoStream(null);
    setIsCameraOpen(false);
  };

  const handleCancelSelfie = () => {
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop());
    }
    setIsCameraOpen(false);
    setPhotoCaptured(false);
    setSelfieUploadSuccess(false);
    setFormData((prevData) => ({
      ...prevData,
      selfieImage: null,
    }));
  };

  const handleUploadSelfie = async () => {
    if (!formData.selfieImage) return;
    setUploading(true);

    try {
      console.log("📤 Uploading Selfie...");
      const selfieBlob = dataURItoBlob(formData.selfieImage);
      const selfieFile = new File([selfieBlob], "selfie.png", {
        type: "image/png",
      });

      await uploadFileToS3(selfieFile);

      const checkUrlInterval = setInterval(() => {
        if (mediaUrls[selfieFile.name]) {
          clearInterval(checkUrlInterval);

          const uploadedUrl = mediaUrls[selfieFile.name];

          setFormData((prevData) => ({
            ...prevData,
            selfieUrl: uploadedUrl,
          }));

          setSelfieUploadSuccess(true);
          setUploading(false);
        }
      }, 500);
    } catch (error) {
      console.error("Selfie Upload Error:", error);
      setUploading(false);
      setErrorMessage("Failed to upload the selfie. Please try again.");
    }
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([arrayBuffer], { type: mimeString });
  };

  const BackButton = ({ onClick }) => (
    <button className={style.backButton} onClick={onClick}>
      <IoIosArrowBack size={22} />
    </button>
  );

  const handleNext = () => {
    if (
      step === 1 &&
      formData.firstName &&
      formData.lastName &&
      formData.phoneNumber &&
      formData.email
    ) {
      setStep(2);
    } else if (
      step === 2 &&
      formData.documentType &&
      formData.documentCountry &&
      formData.documentNumber &&
      formData.documentFile
    ) {
      setStep(3);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setUploading(true);

    if (!formData.documentUrl || !formData.selfieUrl) {
      setErrorMessage("Upload the document and selfie before submitting.");
      setUploading(false);
      return;
    }

    if (
      formData.selfieUrl.includes("undefined") ||
      formData.documentUrl.includes("undefined")
    ) {
      console.error("Invalid URLs detected:", formData);
      setErrorMessage("Upload failed. Please re-upload and try again.");
      setUploading(false);
      return;
    }
    const { documentFile, selfieImage, ...kycData } = formData;

    try {
      submitKyc(
        { partnerId, kycData },
        {
          onSuccess: () => {
            console.log("✅ KYC submitted successfully!");
            setUploading(false);
            navigate("/dashboard/kycdocuments");
          },
          onError: (error) => {
            console.error(
              "Submission failed:",
              error.response ? error.response.data : error
            );

            setErrorMessage(
              `Submission failed: ${error.message || "Unknown error"}`
            );
            setUploading(false);
          },
        }
      );
    } catch (error) {
      console.error("Unexpected error:", error);
      setErrorMessage("An unexpected error occurred. Please try again.");
      setUploading(false);
    }
  };

  const countries = [
    "India",
    "Afghanistan",
    "Albania",
    "Algeria",
    "Andorra",
    "Angola",
    "Antigua and Barbuda",
    "Argentina",
    "Armenia",
    "Australia",
    "Austria",
    "Azerbaijan",
    "Bahamas",
    "Bahrain",
    "Bangladesh",
    "Barbados",
    "Belarus",
    "Belgium",
    "Belize",
    "Benin",
    "Bhutan",
    "Bolivia",
    "Bosnia and Herzegovina",
    "Botswana",
    "Brazil",
    "Brunei",
    "Bulgaria",
    "Burkina Faso",
    "Burundi",
    "Cabo Verde",
    "Cambodia",
    "Cameroon",
    "Canada",
    "Central African Republic",
    "Chad",
    "Chile",
    "China",
    "Colombia",
    "Comoros",
    "Congo (Congo-Brazzaville)",
    "Costa Rica",
    "Croatia",
    "Cuba",
    "Cyprus",
    "Czechia (Czech Republic)",
    "Denmark",
    "Djibouti",
    "Dominica",
    "Dominican Republic",
    "Ecuador",
    "Egypt",
    "El Salvador",
    "Equatorial Guinea",
    "Eritrea",
    "Estonia",
    "Eswatini (Swaziland)",
    "Ethiopia",
    "Fiji",
    "Finland",
    "France",
    "Gabon",
    "Gambia",
    "Georgia",
    "Germany",
    "Ghana",
    "Greece",
    "Grenada",
    "Guatemala",
    "Guinea",
    "Guinea-Bissau",
    "Guyana",
    "Haiti",
    "Holy See",
    "Honduras",
    "Hungary",
    "Iceland",
    "Indonesia",
    "Iran",
    "Iraq",
    "Ireland",
    "Israel",
    "Italy",
    "Jamaica",
    "Japan",
    "Jordan",
    "Kazakhstan",
    "Kenya",
    "Kiribati",
    "Korea (North)",
    "Korea (South)",
    "Kosovo",
    "Kuwait",
    "Kyrgyzstan",
    "Laos",
    "Latvia",
    "Lebanon",
    "Lesotho",
    "Liberia",
    "Libya",
    "Liechtenstein",
    "Lithuania",
    "Luxembourg",
    "Madagascar",
    "Malawi",
    "Malaysia",
    "Maldives",
    "Mali",
    "Malta",
    "Marshall Islands",
    "Mauritania",
    "Mauritius",
    "Mexico",
    "Micronesia",
    "Moldova",
    "Monaco",
    "Mongolia",
    "Montenegro",
    "Morocco",
    "Mozambique",
    "Myanmar (Burma)",
    "Namibia",
    "Nauru",
    "Nepal",
    "Netherlands",
    "New Zealand",
    "Nicaragua",
    "Niger",
    "Nigeria",
    "North Macedonia",
    "Norway",
    "Oman",
    "Pakistan",
    "Palau",
    "Palestine State",
    "Panama",
    "Papua New Guinea",
    "Paraguay",
    "Peru",
    "Philippines",
    "Poland",
    "Portugal",
    "Qatar",
    "Romania",
    "Russia",
    "Rwanda",
    "Saint Kitts and Nevis",
    "Saint Lucia",
    "Saint Vincent and the Grenadines",
    "Samoa",
    "San Marino",
    "Sao Tome and Principe",
    "Saudi Arabia",
    "Senegal",
    "Serbia",
    "Seychelles",
    "Sierra Leone",
    "Singapore",
    "Slovakia",
    "Slovenia",
    "Solomon Islands",
    "Somalia",
    "South Africa",
    "South Sudan",
    "Spain",
    "Sri Lanka",
    "Sudan",
    "Suriname",
    "Sweden",
    "Switzerland",
    "Syria",
    "Tajikistan",
    "Tanzania",
    "Thailand",
    "Timor-Leste",
    "Togo",
    "Tonga",
    "Trinidad and Tobago",
    "Tunisia",
    "Turkey",
    "Turkmenistan",
    "Tuvalu",
    "Uganda",
    "Ukraine",
    "United Arab Emirates",
    "United Kingdom",
    "United States of America",
    "Uruguay",
    "Uzbekistan",
    "Vanuatu",
    "Vatican City",
    "Venezuela",
    "Vietnam",
    "Yemen",
    "Zambia",
    "Zimbabwe",
  ];

  return (
    <div className={style.dashboardFormContainerWrapper}>
      <div className={style.dashboardFormContainer}>
        <h2 className={style.dashboardFormContinerHeading}>
          Complete KYC Verification
        </h2>
        <p className={style.noEquipmentDescription}>
          Complete your KYC verification to ensure security and build trust with
          clients.
        </p>
        <hr className={style.kycTitleDivider} />
        <div className={style.kycFormContent}>
          <div className={style.kycTimeline}>
            {[
              { id: 1, icon: <BsPersonCircle /> },
              { id: 2, icon: <HiClipboardDocumentList /> },
              { id: 3, icon: <TbCameraSelfie /> },
              { id: 4, icon: <ImEye /> },
            ].map((item) => (
              <div
                key={item.id}
                className={`${style.kycStep} ${
                  step === item.id
                    ? style.currentStepIcon
                    : step > item.id
                    ? style.completedStepIcon
                    : ""
                }`}
              >
                <span>{item.icon}</span>
              </div>
            ))}
          </div>

          <div className={style.kycVerticalDivider}></div>

          <div className={style.kycFormSection}>
            {step === 1 && (
              <>
                <h5 className={style.kycStepSubHeading}>Step 1</h5>
                <h3 className={style.kycStepHeading}>Personal Information</h3>
                <div className={style.dashboardFormInputLabelField}>
                  <label className={style.dashFormContainerLabel}>
                    First Name
                  </label>
                  <input
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter First Name"
                    required
                  />
                </div>
                <div className={style.dashboardFormInputLabelField}>
                  <label className={style.dashFormContainerLabel}>
                    Last Name
                  </label>
                  <input
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter Last Name"
                    required
                  />
                </div>
                <div className={style.dashboardFormInputLabelField}>
                  <label className={style.dashFormContainerLabel}>
                    Phone Number
                  </label>
                  <input
                    name="phoneNumber"
                    type="text"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Enter Phone Number"
                    required
                  />
                </div>
                <div className={style.dashboardFormInputLabelField}>
                  <label className={style.dashFormContainerLabel}>Email</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter Email "
                    required
                  />
                </div>
                <button
                  className={style.dashboardFormSaveButtonDown}
                  onClick={handleNext}
                  disabled={
                    !formData.firstName ||
                    !formData.lastName ||
                    !formData.phoneNumber ||
                    !formData.email
                  }
                >
                  Next
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <div className={style.stepHeader}>
                  <h5 className={style.kycStepSubHeading}>Step 2</h5>
                  <BackButton onClick={() => setStep(1)} />
                </div>
                <h3 className={style.kycStepHeading}>ID Verification</h3>
                <div className={style.dashboardFormInputLabelField}>
                  <label className={style.dashFormContainerLabel}>
                    Document Type
                  </label>
                  <Select
                    name="documentType"
                    value={formData.documentType}
                    onChange={handleInputChange}
                    displayEmpty
                    className={style.selectContainer2}
                  >
                    <MenuItem value="" disabled>
                      Choose Document Type
                    </MenuItem>
                    <MenuItem value="Aadhaar Card">Aadhaar Card</MenuItem>
                    <MenuItem value="Driving Licence">Driving Licence</MenuItem>
                    <MenuItem value="Passport">Passport</MenuItem>
                  </Select>
                </div>
                <div className={style.dashboardFormInputLabelField}>
                  <label className={style.dashFormContainerLabel}>
                    Document Issue Country
                  </label>
                  <Select
                    name="documentCountry"
                    value={formData.documentCountry}
                    onChange={handleInputChange}
                    displayEmpty
                    className={style.selectContainer2}
                  >
                    <MenuItem value="" disabled>
                      Choose Country
                    </MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div className={style.dashboardFormInputLabelField}>
                  <label className={style.dashFormContainerLabel}>
                    Document ID Number
                  </label>
                  <input
                    name="documentNumber"
                    type="number"
                    value={formData.documentNumber}
                    onChange={handleInputChange}
                    placeholder="Enter ID Number"
                    required
                  />
                </div>
                <h3 className={style.labels}>Upload Image</h3>
                <div
                  className={style.DragUploader1}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <div
                    className={`${style.DragUploaderImageContainer} ${
                      dragging ? style.dragActive : ""
                    }`}
                  >
                    {!formData.documentFile ? (
                      <>
                        <FaCloudArrowUp className={style.uploadIcon} />
                        <p className={style.uploadText}>Drop images here</p>
                        <p className={style.supportedFormats}>
                          Supported formats: PNG, JPG
                        </p>
                        <p className={style.orText}>OR</p>
                        <h2
                          className={style.browseText}
                          onClick={handleBrowseClick}
                        >
                          <strong>Browse Images</strong>
                        </h2>
                      </>
                    ) : (
                      <img
                        src={uploadPreview}
                        alt="Preview"
                        className={style.imagePreview}
                      />
                    )}

                    <input
                      id="kyc-file-input"
                      type="file"
                      accept="image/png, image/jpeg"
                      className={style.fileInput}
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      hidden
                    />
                  </div>

                  <div className={style.buttonContainer}>
                    <span className={style.cancelText} onClick={handleCancel}>
                      Cancel
                    </span>
                    <button
                      type="button"
                      className={style.uploadButton}
                      onClick={handleUpload}
                      disabled={uploadSuccess}
                    >
                      {uploadSuccess ? "Uploaded" : "Upload"}
                    </button>
                  </div>
                </div>

                {errorMessage && (
                  <p className={style.errorMessage}>{errorMessage}</p>
                )}

                <button
                  className={style.dashboardFormSaveButtonDown}
                  onClick={handleNext}
                  disabled={
                    !formData.documentType ||
                    !formData.documentCountry ||
                    !formData.documentNumber ||
                    !formData.documentFile ||
                    !uploadSuccess
                  }
                >
                  Next
                </button>
              </>
            )}

            {step === 3 && (
              <>
                <div className={style.stepHeader}>
                  <h5 className={style.kycStepSubHeading}>Step 3</h5>
                  <BackButton onClick={() => setStep(2)} />
                </div>
                <h3 className={style.kycStepHeading}>Self Verification</h3>

                <div className={style.dashboardFormInputLabelField}>
                  <label className={style.dashFormContainerLabel}>
                    Take a Picture
                  </label>

                  <div className={style.selfieContainer}>
                    {isCameraOpen ? (
                      <video
                        id="selfie-video"
                        autoPlay
                        playsInline
                        className={style.selfieVideo}
                      ></video>
                    ) : formData.selfieImage ? (
                      <img
                        src={formData.selfieImage}
                        alt="Selfie"
                        className={style.selfiePreview}
                      />
                    ) : (
                      <div className={style.selfiePlaceholder}>
                        Your captured image will appear here
                      </div>
                    )}
                  </div>
                </div>

                {!isCameraOpen && !photoCaptured ? (
                  <button
                    className={style.uploadButtonSelfie}
                    onClick={handleTakeSelfie}
                  >
                    Open Camera
                  </button>
                ) : (
                  <button
                    className={style.uploadButtonSelfie}
                    onClick={handleCapturePhoto}
                    disabled={photoCaptured}
                  >
                    {photoCaptured ? "Photo Captured" : "Capture Photo"}
                  </button>
                )}

                {photoCaptured && (
                  <div className={style.buttonContainer}>
                    <span
                      className={style.cancelText}
                      onClick={handleCancelSelfie}
                    >
                      Cancel
                    </span>
                    <button
                      type="button"
                      className={style.uploadButton}
                      onClick={handleUploadSelfie}
                      disabled={selfieUploadSuccess}
                    >
                      {selfieUploadSuccess ? "Uploaded" : "Upload"}
                    </button>
                  </div>
                )}

                <button
                  className={style.dashboardFormSaveButtonDown}
                  onClick={() => setStep(4)}
                  disabled={!selfieUploadSuccess}
                >
                  Next
                </button>
              </>
            )}

            {step === 4 && (
              <>
                <div className={style.stepHeader}>
                  <h5 className={style.kycStepSubHeading}>Step 4</h5>
                  <BackButton onClick={() => setStep(3)} />
                </div>
                <h3 className={style.kycStepHeading}>Review Application</h3>

                <div className={style.reviewContainer}>
                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>First Name:</strong>{" "}
                    <span>{formData.firstName}</span>
                  </div>
                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>Last Name:</strong>{" "}
                    <span>{formData.lastName}</span>
                  </div>
                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>Phone Number:</strong>{" "}
                    <span>{formData.phoneNumber}</span>
                  </div>
                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>Email:</strong>{" "}
                    <span>{formData.email}</span>
                  </div>
                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>Document Type:</strong>{" "}
                    <span>{formData.documentType}</span>
                  </div>
                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>
                      Document Issue Country:
                    </strong>{" "}
                    <span>{formData.documentCountry}</span>
                  </div>
                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>
                      Document ID Number:
                    </strong>{" "}
                    <span>{formData.documentNumber}</span>
                  </div>

                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>
                      Uploaded Document:
                    </strong>
                    {formData.documentFile ? (
                      <img
                        src={URL.createObjectURL(formData.documentFile)}
                        alt="Uploaded Document"
                        className={style.uploadedImagePreview}
                      />
                    ) : (
                      <span>No Document Uploaded</span>
                    )}
                  </div>

                  <div className={style.reviewItem}>
                    <strong className={style.reviewKey}>Selfie:</strong>
                    {formData.selfieImage ? (
                      <img
                        src={formData.selfieImage}
                        alt="Selfie Preview"
                        className={style.selfiePreviewReview}
                      />
                    ) : (
                      <span>No Selfie Captured</span>
                    )}
                  </div>
                </div>

                <button
                  className={style.dashboardFormSaveButtonDown}
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default KycDocumentsForm;
